import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Radio as MuiRadio, RadioGroup as MuiRadioGroup, type RadioGroupProps } from '@mui/material'
import { forwardRef } from 'react'

import { borderStroke, colorTokens } from '../../../foundations/index.ts'

const RadioCommon = styled.span<Pick<RadioProps, 'size'>>((props) => ({
  borderRadius: '50%',
  width: props.size === 'small' ? 16 : 24,
  height: props.size === 'small' ? 16 : 24,
  '.Mui-focusVisible &': {
    outline: `${borderStroke[20]} solid ${colorTokens['color-stroke-base-input-focus']}`,
    boxShadow: 'none',
  },
}))

const RadioUnselected = styled(RadioCommon)(() => ({
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,0),hsla(0,0%,100%,0))',
  outline: `${borderStroke[20]} solid ${colorTokens['color-stroke-base-input']}`,
  boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.03) inset',
  backgroundColor: `${colorTokens['color-bg-base-normal']}`,

  'input:enabled:hover ~ &': {
    boxShadow: `0px 0px 0px 4px ${colorTokens['color-stroke-base-input-highlight']}`,
    backgroundColor: colorTokens['color-bg-base-hover'],
  },

  'input:disabled ~ &': {
    backgroundColor: `${colorTokens['color-bg-base-disable']}`,
    boxShadow: `0px 1px 3px 0px rgba(0, 0, 0, 0.03) inset`,
    outline: `${borderStroke[20]} solid ${colorTokens['color-stroke-base-input-disable']}`,
  },
}))

const RadioSelected = styled(RadioCommon)((props) => ({
  backgroundColor: `${colorTokens['color-bg-base-active']}`,
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&:before': {
    display: 'block',
    width: props.size === 'small' ? 16 : 24,
    height: props.size === 'small' ? 16 : 24,
    backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
    content: '""',
  },
  'input:hover:enabled ~ &': {
    boxShadow: `0px 0px 0px 4px ${colorTokens['color-stroke-base-input-highlight']}`,
  },
  'input:disabled ~ &': {
    backgroundColor: `${colorTokens['color-bg-base-active-disable']}`,
    boxShadow: `0px 1px 3px 0px rgba(0, 0, 0, 0.03) inset`,
  },
}))

type RadioProps = {
  id?: string | undefined
  value: string
  size: 'small' | 'medium'
  className?: string | undefined
  disabled?: boolean
  checked?: boolean
  onChange?: () => void
  ['aria-label']?: string | undefined
}

export const Radio = forwardRef<HTMLButtonElement, RadioProps>(function Radio(props, ref) {
  return (
    <MuiRadio
      checkedIcon={<RadioSelected size={props.size} />}
      icon={<RadioUnselected size={props.size} />}
      value={props.value}
      css={css`
        padding: 0;
      `}
      {...(props.className ? { className: props.className } : {})}
      {...(props.disabled != null ? { disabled: props.disabled } : {})}
      {...(ref ? { inputRef: ref } : {})}
      {...(props.checked != null ? { checked: props.checked } : {})}
      {...(props.onChange ? { onChange: props.onChange } : {})}
      slotProps={{
        input: {
          id: props.id,
          'aria-label': props['aria-label'],
        },
      }}
    />
  )
})

export const RadioGroup = forwardRef<HTMLElement, RadioGroupProps>(function RadioGroup(props, ref) {
  return <MuiRadioGroup ref={ref} {...props} />
})
